/**
 * Axios 클라이언트 — 세션 쿠키 (kag.sid) 자동 전송 + 표준 에러 envelope 처리.
 *
 * www 와 동일 패턴이지만 admin 자체 인스턴스 (BaseURL / 인터셉터 분리 가능).
 */
import axios, { type AxiosError } from 'axios';
import { useToastStore } from '@/stores/toast';

export interface ApiErrorEnvelope {
  code: string;
  message: string;
  field?: string;
  action?: { label: string; href: string };
  meta?: Record<string, unknown>;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly httpStatus: number,
    public readonly field?: string,
    public readonly action?: { label: string; href: string },
    public readonly meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    // Tell the API to use the admin-specific session cookie
    // (kag.sid.admin) so admin and www can't overwrite each other.
    'X-App-Surface': 'admin',
  },
});

/**
 * 파일을 올릴 때는 JSON 기본값을 걷어낸다.
 *
 * 이 인스턴스는 Content-Type 을 application/json 으로 못박아 두는데, 몸통이
 * FormData 이면 axios 가 그 헤더를 보고 FormData 를 JSON 문자열로 바꿔 버린다.
 * 그러면 boundary 가 없는 application/json 요청이 나가고, 서버 multipart
 * 파서는 'Expected multipart/form-data with boundary.' 로 거절한다.
 * 헤더를 지우면 브라우저가 boundary 를 붙여 준다.
 */
http.interceptors.request.use((config) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  return config;
});

/**
 * 세션이 끊겼을 때 부를 콜백. main.ts 가 라우터를 아는 쪽에서 등록한다.
 *
 * 여기서 직접 router 를 import 하면 router → pages → api/http → router 로
 * 순환이 생긴다.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/** 한 번만 보낸다 — 병렬 요청이 동시에 401 이어도 이동은 한 번이다. */
let redirecting = false;

http.interceptors.response.use(
  (response) => {
    // 성공한 요청이 하나라도 있으면 세션이 살아난 것이다.
    redirecting = false;
    return response;
  },
  (error: AxiosError<{ error?: ApiErrorEnvelope }>) => {
    const envelope = error.response?.data?.error;

    // 401 은 '먼저 할 일'이 아니라 '로그인이 끝났다'는 뜻이다.
    //
    // 서버는 이 오류에도 action {label:'Sign in'} 을 실어 보내는데, 그것을
    // 아래의 일반 규칙으로 처리하면 토스트가 뜬다. 화면에 여러 요청이 떠
    // 있으면 그 수만큼 토스트가 쌓이고, 정작 화면은 그 자리에 남아 사용자가
    // 토스트를 하나씩 닫아야 한다. 로그인 화면으로 보내는 것이 맞다.
    if (error.response?.status === 401) {
      if (!redirecting) {
        redirecting = true;
        onUnauthorized?.();
      }
      return Promise.reject(
        new ApiError(
          envelope?.code ?? 'UNAUTHENTICATED',
          envelope?.message ?? 'Login required.',
          401,
        ),
      );
    }

    if (envelope) {
      // Surface actionable prerequisite errors globally — any error whose
      // backend envelope carries an action {label, href} pops a toast with a
      // clickable "do X first" button, without per-screen wiring. Guarded so a
      // very-early failure (before pinia is ready) can't break error handling.
      if (envelope.action) {
        try {
          useToastStore().push(envelope.message, envelope.action);
        } catch {
          // pinia not active yet — fall through to normal rejection
        }
      }
      return Promise.reject(
        new ApiError(
          envelope.code,
          envelope.message,
          error.response?.status ?? 0,
          envelope.field,
          envelope.action,
          envelope.meta,
        ),
      );
    }
    if (error.code === 'ECONNABORTED') {
      return Promise.reject(
        new ApiError('TIMEOUT', 'The request timed out. Please try again.', 0),
      );
    }
    if (!error.response) {
      return Promise.reject(
        new ApiError('NETWORK_ERROR', 'Cannot reach the server. Check your connection.', 0),
      );
    }
    return Promise.reject(
      new ApiError(
        'UNKNOWN_ERROR',
        `An unexpected error occurred. (HTTP ${error.response.status})`,
        error.response.status,
      ),
    );
  },
);
